import React, { useEffect, useState } from "react";
import { saveAs } from "file-saver";
import { DownloadOutlined, WalletOutlined } from "@ant-design/icons";
import { showMessage } from "../../Functionalities/Functions";
import confirmBox from "../../CoreComponents/confirmBox";
import { fmtDate, rupees } from "../Insights/format";
import PaymentsTable from "../DeliveryBoys/PaymentsTable";
import { deletePayment, fetchAllPayments, fetchPayments, paymentsCsv } from "../DeliveryBoys/riderPaymentsData";
import Stat from "../Settlements/Stat";
import PaymentsRangeFilter from "./PaymentsRangeFilter";
import CollectMoneyModal from "./CollectMoneyModal";

export const PAGE_SIZE = 50;
const EMPTY = { rows: [], count: 0, total: 0 };

export const pageLabel = (page, count) =>
  count ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, count)} of ${count}` : "No payments";

export default function CollectionsList() {
  const [range, setRange] = useState(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const [collecting, setCollecting] = useState(false);

  const collected = () => {
    setCollecting(false);
    setPage(1);
    setVersion((v) => v + 1);
  };

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchPayments({ range, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }).then((result) => {
      if (!live) return;
      setData(result);
      setLoading(false);
      if (result.error) showMessage(result.error, "danger", "Could not load the payments", "top", "top-right");
    });
    return () => {
      live = false;
    };
  }, [range, page, version]);

  const search = (next) => {
    setPage(1);
    setRange(next);
    setVersion((v) => v + 1);
  };

  const download = async () => {
    const rows = await fetchAllPayments(range);
    const name = range ? `rider-settlements-${range.from}-to-${range.to}.csv` : "rider-settlements.csv";
    saveAs(new Blob([paymentsCsv(rows)], { type: "text/csv;charset=utf-8" }), name);
  };

  const remove = (payment) => {
    const who = payment.rider.name || `Rider #${payment.rider.id}`;
    confirmBox({
      title: `Delete ${who}'s payment?`,
      message: `${rupees(payment.amount)} paid on ${fmtDate(payment.paid_on)}. The rider holds that cash again.`,
      yesLabel: "Delete",
      noLabel: "Cancel",
      onYes: () => removeConfirmed(payment),
    });
  };

  const removeConfirmed = async (payment) => {
    const body = await deletePayment(payment.id);
    if (body.Status !== true) {
      showMessage(body.Error || "The payment was not deleted", "danger", "Not deleted", "top", "top-right");
      return;
    }
    setVersion((v) => v + 1);
  };

  const lastPage = Math.max(1, Math.ceil(data.count / PAGE_SIZE));

  return (
    <div className="container-fluid stl">
      <section className="stl-card stl-toolbar">
        <PaymentsRangeFilter applied={range} loading={loading} onSearch={search} />
        <span className="stl-toolbar__end">
          <button type="button" className="stl-btn stl-btn--secondary" disabled={!data.count} onClick={download}>
            <DownloadOutlined /> Download CSV
          </button>
          <button type="button" className="stl-btn stl-btn--primary" onClick={() => setCollecting(true)}>
            <WalletOutlined /> Rider Collection
          </button>
        </span>
      </section>
      <CollectMoneyModal open={collecting} onClose={() => setCollecting(false)} onSaved={collected} />

      <section className="stl-stats">
        <Stat label="Received" value={rupees(data.total)} hint={range ? `${fmtDate(range.from)} to ${fmtDate(range.to)}` : "all time"} />
        <Stat label="Payments" value={data.count} />
      </section>

      <section className="stl-card stl-table-card">
        <div className="stl-table-bar">
          <div className="stl-table-bar__title">
            <h2 className="stl-title">Recent collections</h2>
            <span className="stl-subtitle">{pageLabel(page, data.count)}</span>
          </div>
          {lastPage > 1 && (
            <div className="stl-table-bar__tools">
              <button type="button" className="stl-btn stl-btn--sm stl-btn--secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
                Previous
              </button>
              <button type="button" className="stl-btn stl-btn--sm stl-btn--secondary" disabled={page === lastPage} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          )}
        </div>
        <PaymentsTable rows={data.rows} loading={loading} showRider onDelete={remove} emptyText={data.error || "No rider has paid in this period"} />
      </section>
    </div>
  );
}
